// Team-wide evidence usage aggregate. Auth is read here; `teamId` stays an
// argument and the second element of the cache identity. Folding an absent id
// into the gate would GET `/user-activity/team/undefined/evidence`.
import { skipToken, useQuery } from '@tanstack/react-query';

import { TEAM_EVIDENCE_USAGE_STATS_QUERY_KEY } from '../../contracts';
import { getTeamEvidenceUsageStats } from '../api/userActivity';
import { useIsAuthenticated } from '../injected';

/**
 * GET /api/v2/ml-scribe/user-activity/team/{team_id}/evidence
 *
 * Aggregate evidence usage stats summed across every member of a team — used
 * on team-admin surfaces such as the 90-day trial recap modal so the recap
 * reflects the whole practice's engagement, not just the admin's personal
 * usage.
 *
 * The endpoint returns 403 for non-admin callers, so consumers must gate the
 * call upstream by passing `enabled: false` when the current user isn't an
 * admin of the team.
 */
export function useQueryTeamEvidenceUsageStats(
  teamId: string | undefined,
  enabled: boolean
) {
  const canRead = useIsAuthenticated() && enabled;

  return useQuery({
    queryKey: [TEAM_EVIDENCE_USAGE_STATS_QUERY_KEY, { teamId }],
    queryFn:
      canRead && teamId ? async () => getTeamEvidenceUsageStats(teamId) : skipToken,
    staleTime: 60 * 1000,
  });
}
